import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Code2, Send, MessageSquare, Key } from "lucide-react";
import { useBranding } from "@/hooks/use-branding";

export default function ApiDocsPage() {
  const baseUrl = window.location.origin;
  const branding = useBranding();

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-api-docs-title">API Documentation</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Integrate {branding.productName} into any platform using our REST API
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="w-5 h-5" />
            Authentication
          </CardTitle>
          <CardDescription>
            All API requests require authentication using a Bearer token
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            Each chatbot has a unique API key. Include it in the Authorization header:
          </p>
          <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`Authorization: Bearer mb_your_api_key_here`}
          </pre>
          <p className="text-sm text-muted-foreground">
            You can find your chatbot's API key in the chatbot settings under the API tab.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send Message
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge>POST</Badge>
            <code className="text-sm font-mono text-muted-foreground min-w-0 max-w-full break-all">/api/v1/chat</code>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h2 className="text-sm font-medium mb-2">Request Body</h2>
            <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`{
  "message": "string (required) - The user's message",
  "session_id": "string (required) - Unique session identifier for conversation tracking"
}`}
            </pre>
          </div>

          <div>
            <h2 className="text-sm font-medium mb-2">Response</h2>
            <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`{
  "reply": "string - The AI assistant's response",
  "conversation_id": "string - Unique conversation ID",
  "session_id": "string - Echo of the session ID"
}`}
            </pre>
          </div>

          <Tabs defaultValue="curl">
            <TabsList>
              <TabsTrigger value="curl">cURL</TabsTrigger>
              <TabsTrigger value="js">JavaScript</TabsTrigger>
              <TabsTrigger value="python">Python</TabsTrigger>
            </TabsList>
            <TabsContent value="curl">
              <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`curl -X POST ${baseUrl}/api/v1/chat \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer mb_your_api_key" \\
  -d '{"message": "Hello!", "session_id": "session-123"}'`}
              </pre>
            </TabsContent>
            <TabsContent value="js">
              <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`const response = await fetch("${baseUrl}/api/v1/chat", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer mb_your_api_key"
  },
  body: JSON.stringify({
    message: "Hello!",
    session_id: "session-123"
  })
});

const data = await response.json();
console.log(data.reply);`}
              </pre>
            </TabsContent>
            <TabsContent value="python">
              <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`import requests

response = requests.post(
    "${baseUrl}/api/v1/chat",
    headers={
        "Content-Type": "application/json",
        "Authorization": "Bearer mb_your_api_key"
    },
    json={
        "message": "Hello!",
        "session_id": "session-123"
    }
)

data = response.json()
print(data["reply"])`}
              </pre>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Get Conversation History
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge variant="secondary">GET</Badge>
            <code className="text-sm font-mono text-muted-foreground min-w-0 max-w-full break-all">/api/v1/conversations/:session_id</code>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2">Response</h4>
            <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`{
  "messages": [
    {
      "role": "user",
      "content": "Hello!",
      "created_at": "2024-01-01T00:00:00Z"
    },
    {
      "role": "assistant", 
      "content": "Hi! How can I help?",
      "created_at": "2024-01-01T00:00:01Z"
    }
  ]
}`}
            </pre>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Code2 className="w-5 h-5" />
            Embed Widget
          </CardTitle>
          <CardDescription>
            Add a chat widget to any website with a simple script tag
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="api-code-block p-4 rounded-md bg-muted text-xs font-mono">
{`<script>
  (function() {
    var s = document.createElement('script');
    s.src = '${baseUrl}/embed.js';
    s.setAttribute('data-api-key', 'mb_your_api_key');
    s.setAttribute('data-color', '#0c3547');
    document.body.appendChild(s);
  })();
</script>`}
          </pre>
          <p className="text-sm text-muted-foreground mt-3">
            The widget will appear as a floating chat button in the bottom-right corner of your website.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Error Codes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[
              { code: "400", desc: "Bad Request - Missing required fields" },
              { code: "401", desc: "Unauthorized - Invalid or missing API key" },
              { code: "404", desc: "Not Found - Chatbot not found or inactive" },
              { code: "500", desc: "Server Error - OpenAI API key may not be configured" },
            ].map((err) => (
              <div key={err.code} className="flex flex-wrap items-center gap-3 p-3 rounded-md bg-muted/50">
                <Badge variant={err.code === "500" ? "destructive" : "outline"}>{err.code}</Badge>
                <span className="text-sm">{err.desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
